/**
 * Capacitor Service - Abstraction layer for Capacitor plugins
 * Provides a clean interface for native mobile features with web fallbacks
 */

import { Capacitor } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { Preferences } from '@capacitor/preferences';
import { Network } from '@capacitor/network';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { LoggingService } from './logging';

export interface GeolocationPosition {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    speed: number | null;
  };
  timestamp: number;
}

export interface GeolocationResult {
  position: GeolocationPosition;
  /** Whether high accuracy was used (false if we fell back to low accuracy) */
  usedHighAccuracy: boolean;
}

export interface NetworkStatus {
  connected: boolean;
  connectionType: string;
}

export interface DeviceInfo {
  model: string;
  platform: string;
  operatingSystem: string;
  osVersion: string;
  manufacturer: string;
  isVirtual: boolean;
  webViewVersion: string;
}

export const CapacitorService = {
  /**
   * Check if running as a native app
   */
  isNative(): boolean {
    return Capacitor.isNativePlatform();
  },

  /**
   * Get the current platform (web, ios, android)
   */
  getPlatform(): string {
    return Capacitor.getPlatform();
  },

  /**
   * Geolocation methods
   */
  geolocation: {
    /**
     * Get current position with accuracy info
     * Automatically falls back to low accuracy if high accuracy fails (e.g., on devices without GPS)
     * Returns both the position and whether high accuracy was used
     */
    async getCurrentPositionWithInfo(
      options?: PositionOptions
    ): Promise<GeolocationResult> {
      // On devices without GPS (like Amazon tablets), high accuracy times out.
      // We try high accuracy first, then fall back to low accuracy if it fails.
      const enableHighAccuracy = options?.enableHighAccuracy ?? true;
      const timeout = options?.timeout ?? 15000;
      const maximumAge = options?.maximumAge ?? 0;
      let position: Position | null = null;
      let usedHighAccuracy = enableHighAccuracy;

      try {
        position = await Geolocation.getCurrentPosition({
          enableHighAccuracy,
          timeout,
          maximumAge,
        });
      } catch (error: any) {
        LoggingService.Log(`Error getting current position: ${error.message}, retrying with enableHighAccuracy: false`);
        await new Promise(resolve => setTimeout(resolve, 500));
        position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout,
          maximumAge,
        });
        usedHighAccuracy = false;
      }
      LoggingService.Log(`Current position: ${position.coords.latitude}, ${position.coords.longitude} (highAccuracy: ${usedHighAccuracy})`);
      return {
        position: {
          coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
            heading: position.coords.heading,
            speed: position.coords.speed,
          },
          timestamp: position.timestamp,
        },
        usedHighAccuracy,
      };
    },

    /**
     * Get current position (convenience method)
     * Automatically falls back to low accuracy if high accuracy fails
     */
    async getCurrentPosition(
      options?: PositionOptions
    ): Promise<GeolocationPosition> {
      const result = await this.getCurrentPositionWithInfo(options);
      return result.position;
    },

    /**
     * Watch position changes
     * Returns a watch ID that can be used to clear the watch
     */
    async watchPosition(
      callback: (position: GeolocationPosition, err?: any) => void,
      options?: PositionOptions
    ): Promise<string> {
      const watchId = await Geolocation.watchPosition(
        {
          enableHighAccuracy: options?.enableHighAccuracy ?? true,
          timeout: options?.timeout ?? 15000,
          maximumAge: options?.maximumAge ?? 0,
        },
        (position, err) => {
          if (err) {
            callback(null as any, err);
            return;
          }

          if (position) {
            callback({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
                heading: position.coords.heading,
                speed: position.coords.speed,
              },
              timestamp: position.timestamp,
            });
          }
        }
      );

      return watchId;
    },

    /**
     * Clear a position watch
     */
    async clearWatch(watchId: string): Promise<void> {
      await Geolocation.clearWatch({ id: watchId });
    },

    /**
     * Request location permissions
     */
    async requestPermissions(): Promise<boolean> {
      try {
        // Not implemented for web, it will come up when we get their location.
        if (!Capacitor.isNativePlatform()) {
          return true;
        }
        const result = await Geolocation.requestPermissions();
        return (
          result.location === 'granted' ||
          result.coarseLocation === 'granted'
        );
      } catch (error) {
        console.error('Error requesting location permissions:', error);
        return false;
      }
    },

    /**
     * Check location permissions
     */
    async checkPermissions(): Promise<string> {
      try {
        const result = await Geolocation.checkPermissions();
        return result.location;
      } catch (error) {
        console.error('Error checking location permissions:', error);
        return 'denied';
      }
    },
  },

  /**
   * Storage/Preferences methods
   * Replaces localStorage with native storage
   */
  storage: {
    /**
     * Set a value in storage
     */
    async set(key: string, value: string): Promise<void> {
      await Preferences.set({ key, value });
    },

    /**
     * Get a value from storage
     */
    async get(key: string): Promise<string | null> {
      const { value } = await Preferences.get({ key });
      return value;
    },

    /**
     * Remove a value from storage
     */
    async remove(key: string): Promise<void> {
      await Preferences.remove({ key });
    },

    /**
     * Clear all storage
     */
    async clear(): Promise<void> {
      await Preferences.clear();
    },

    /**
     * Get all keys
     */
    async keys(): Promise<string[]> {
      const { keys } = await Preferences.keys();
      return keys;
    },
  },

  /**
   * Network methods
   */
  network: {
    /**
     * Get current network status
     */
    async getStatus(): Promise<NetworkStatus> {
      const status = await Network.getStatus();
      return {
        connected: status.connected,
        connectionType: status.connectionType,
      };
    },

    /**
     * Add listener for network status changes
     */
    addListener(
      callback: (status: NetworkStatus) => void
    ): Promise<{ remove: () => void }> {
      return Network.addListener('networkStatusChange', (status) => {
        callback({
          connected: status.connected,
          connectionType: status.connectionType,
        });
      });
    },
  },

  /**
   * App methods
   */
  app: {
    /**
     * Get app info
     */
    async getInfo(): Promise<{ id: string; name: string; version: string; build: string }> {
      const info = await App.getInfo();
      return info;
    },

    /**
     * Add listener for app state changes
     */
    addStateChangeListener(
      callback: (state: { isActive: boolean }) => void
    ): Promise<{ remove: () => void }> {
      return App.addListener('appStateChange', callback);
    },

    /**
     * Exit the app (Android only)
     */
    async exitApp(): Promise<void> {
      if (Capacitor.getPlatform() === 'android') {
        await App.exitApp();
      }
    },
  },

  /**
   * Device methods
   */
  device: {
    /**
     * Get device information
     */
    async getInfo(): Promise<DeviceInfo> {
      const info = await Device.getInfo();
      return {
        model: info.model,
        platform: info.platform,
        operatingSystem: info.operatingSystem,
        osVersion: info.osVersion,
        manufacturer: info.manufacturer,
        isVirtual: info.isVirtual,
        webViewVersion: info.webViewVersion,
      };
    },

    /**
     * Get device ID
     */
    async getId(): Promise<string> {
      const { identifier } = await Device.getId();
      return identifier;
    },

    /**
     * Get battery info
     */
    async getBatteryInfo(): Promise<{ batteryLevel?: number; isCharging?: boolean }> {
      const info = await Device.getBatteryInfo();
      return {
        batteryLevel: info.batteryLevel,
        isCharging: info.isCharging,
      };
    },
  },

  /**
   * Status Bar methods (iOS and Android only)
   */
  statusBar: {
    /**
     * Set status bar style
     */
    async setStyle(style: 'dark' | 'light'): Promise<void> {
      if (!Capacitor.isNativePlatform()) return;

      await StatusBar.setStyle({
        style: style === 'dark' ? Style.Dark : Style.Light,
      });
    },

    /**
     * Set status bar background color (Android only)
     */
    async setBackgroundColor(color: string): Promise<void> {
      if (Capacitor.getPlatform() !== 'android') return;

      await StatusBar.setBackgroundColor({ color });
    },

    /**
     * Show status bar
     */
    async show(): Promise<void> {
      if (!Capacitor.isNativePlatform()) return;

      await StatusBar.show();
    },

    /**
     * Hide status bar
     */
    async hide(): Promise<void> {
      if (!Capacitor.isNativePlatform()) return;

      await StatusBar.hide();
    },
  },

  /**
   * Splash Screen methods
   */
  splashScreen: {
    /**
     * Show splash screen
     */
    async show(): Promise<void> {
      if (!Capacitor.isNativePlatform()) return;

      await SplashScreen.show({
        autoHide: false,
      });
    },

    /**
     * Hide splash screen
     */
    async hide(): Promise<void> {
      if (!Capacitor.isNativePlatform()) return;

      await SplashScreen.hide();
    },
  },
};

export default CapacitorService;
